import { array } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonSkeletonBarLoaderComponent from 'webapp/components/common/skeleton-bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import GoalsProgressComponent from 'webapp/components/goals/progress';
import GroupSelectComponent from 'webapp/components/group-select';
import OwnerSelectComponent from 'webapp/components/owner-select';

import type { Goal } from 'webapp/models-custom/index';

interface Args {
	model: Goal;
	onChange: (id: number, node: HTMLElement) => void;
}

export default class GoalsTreeItemComponent extends Component<Args> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	mutationObserver: MutationObserver | null = null;

	get model() {
		return this.args.model;
	}

	get goalData() {
		return this.model.hierarchyDataModelWithFallback!;
	}

	@action
	bindMutationObserver(node: HTMLElement) {
		const observer = new MutationObserver(() => {
			this.args.onChange(this.model.numericId!, node.parentNode as HTMLElement);
		});
		observer.observe(node, {
			childList: true,
			attributes: true,
			characterData: true,
			subtree: true,
		});
		this.mutationObserver = observer;
	}

	@action
	unbindMutationObserver() {
		const mutationObserver = this.mutationObserver;
		if (mutationObserver) {
			mutationObserver.disconnect();
			this.mutationObserver = null;
		}
	}

	<template>
		<div
			data-id={{@model.id}}
			class={{joinClasses
				(array
					"card"
					"card--visualization"
					"card--goal"
					"flex-item"
					"filter-sort-item"
					this.goalData.currentProgressClass
				)
			}}
			{{didInsert this.bindMutationObserver}}
			{{willDestroy this.unbindMutationObserver}}
		>
			<div class="info">
				<div class="anti-stretch">
					<EditableModelPropertyComponent @model={{@model}} @canEdit={{false}} />
					<div class="subtitle">
						{{#if @model.isObjectiveType}}
							<span class="target">
								<CommonUiIconComponent @name="Flag" @size="sm" />
							</span>
							Objective
						{{else}}
							Goal
						{{/if}}
						<span class="dot">&centerdot;</span>
						{{@model.timeframeText}}
					</div>
				</div>
			</div>

			<div class="visualization goal-progress-wrapper">
				<div class="number">
					{{#if this.goalData.isLoadingData}}
						<CommonSkeletonBarLoaderComponent @type="number" @position="center" />
					{{else if this.goalData.comparePoint.changeStr}}
						<div class="wrap">
							<div class="val">
								{{this.goalData.comparePoint.changeStr}}
							</div>
						</div>
					{{/if}}
				</div>

				<GoalsProgressComponent
					@model={{@model}}
					@goalData={{this.goalData}}
					@size="small"
				/>

				<div class="overlay">
					<a role="button" class="button">View</a>
				</div>
			</div>

			<div class="bottom">
				<OwnerSelectComponent @model={{@model}} @canEdit={{false}} />
				<GroupSelectComponent @model={{@model}} @canEdit={{false}} />
			</div>
		</div>
	</template>
}
